import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { object as yupObject, ref as yupRef, string as yupString } from "yup";

import {
  Button,
  CheckboxGroup,
  Modal,
  TextInput,
  addToast,
} from "@icg360/design-system";

import { useKeystone, useUser } from "data/ss-data-hooks";
import { isError } from "data/ss-error";
import { trackEvent } from "utils";

import styles from "./contact-item.module.scss";

const emailSettingsSchema = yupObject()
  .shape({
    emailAddress: yupString()
      .required("Please enter an email address.")
      .email("Please enter a valid email address."),
    emailAddressConfirm: yupString().when("emailAddress", {
      is: (val) => val?.length > 0,
      then: (schema) =>
        schema
          .oneOf(
            [yupRef("emailAddress"), undefined, ""],
            "Email addresses must match.",
          )
          .required("Please enter an email address."),
    }),
  })
  .required();

type EditContactEmailFormFields = {
  emailAddress: string;
  emailAddressConfirm?: string;
  // applyToPaperless is being tracked outside react-hook-forms because DS Checkbox doesn't work with it nicely, yet
  // applyToPaperless: boolean;
};

type EditContactEmailButtonProps = {
  policyId: string;
  userEmail: string;
  isEnrolledBilling: boolean;
  isEnrolledDocuments: boolean;
  onSuccess: (update: string) => void;
};

export const EditContactEmailButton = ({
  policyId,
  userEmail,
  isEnrolledBilling,
  isEnrolledDocuments,
  onSuccess,
}: EditContactEmailButtonProps) => {
  const keystone = useKeystone();
  const user = useUser();

  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [applyToPaperless, setApplyToPaperless] = useState(false);

  const {
    handleSubmit,
    register,
    formState: { errors, isValid },
    reset,
  } = useForm<EditContactEmailFormFields>({
    resolver: yupResolver(emailSettingsSchema),
    mode: "onBlur",
  });

  const onSave = async ({ emailAddress }) => {
    if (!user) return;
    setSaving(true);
    let paperlessUpdateSucceeded = true;

    try {
      const res = await keystone.updateInsuredInfo(
        policyId,
        userEmail,
        user.userId,
        {
          email: emailAddress,
        },
      );
      const updateSuccess = !isError(res);
      if (updateSuccess) {
        onSuccess(emailAddress);
      } else {
        throw res;
      }
      if (updateSuccess && applyToPaperless) {
        if (isEnrolledBilling) {
          const res = keystone.updatePaperlessBillingEmail(
            policyId,
            emailAddress,
          );
          paperlessUpdateSucceeded = !isError(res);
        }

        if (isEnrolledDocuments) {
          const res = keystone.updatePaperlessDocsEmail(policyId, emailAddress);
          paperlessUpdateSucceeded = paperlessUpdateSucceeded && !isError(res);
        }
      }

      if (updateSuccess && paperlessUpdateSucceeded) {
        addToast("Email address updated", { icon: true });
        reset();
        trackEvent("Email Address Updated", {
          updatePaperlessEmail:
            isEnrolledBilling || isEnrolledDocuments ? applyToPaperless : null,
        });
        setShowModal(false);
      } else {
        trackEvent("Error Displayed (Update Contact Email)");
        addToast("We apologize. Something went wrong.", {
          icon: true,
          error: true,
        });
      }
      setSaving(false);
    } catch (err) {
      trackEvent("Error Displayed (Update Contact Email)", err);
      addToast("We apologize. Something went wrong.", {
        icon: true,
        error: true,
      });
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        appearance="link"
        onPress={() => setShowModal(true)}
        data-testid="contact-email-edit-btn"
      >
        Edit
      </Button>
      <Modal
        open={showModal}
        heading="Update your email"
        onClose={() => setShowModal(false)}
        primaryAction="Save"
        onPrimaryAction={handleSubmit(onSave)}
        loadingPrimary={saving}
        disablePrimary={!isValid}
      >
        <div className={styles.modalContent}>
          <div>
            This email is used for claims updates, payment reminders, and
            notifications about your account. It&apos;s separate from your login
            email.
          </div>
          <TextInput
            label="New email"
            {...register("emailAddress")}
            errorMessage={errors.emailAddress?.message}
            isError={!!errors.emailAddress}
          />
          <TextInput
            label="Confirm email"
            {...register("emailAddressConfirm")}
            errorMessage={errors.emailAddressConfirm?.message}
            isError={!!errors.emailAddressConfirm}
          />
          {isEnrolledBilling ? (
            <CheckboxGroup
              aria-label={
                !isEnrolledDocuments
                  ? "Receive policy documents and billing statements."
                  : "Receive billing statements."
              }
              helpMessage={
                !isEnrolledDocuments
                  ? "This will update all emails in paperless settings."
                  : "This will update your existing billing email."
              }
            >
              <CheckboxGroup.Checkbox
                onChange={(value) => setApplyToPaperless(value)}
              >
                {!isEnrolledDocuments
                  ? "Receive policy documents and billing statements."
                  : "Receive billing statements."}
              </CheckboxGroup.Checkbox>
            </CheckboxGroup>
          ) : null}
        </div>
      </Modal>
    </>
  );
};
